import { useState } from "react";

import * as api from "../api/status";
import { ApiException } from "../api/client";

type Phase =
  | { kind: "idle" }
  | { kind: "verify_pending"; result: api.SubscribeResult }
  | { kind: "verified" };

function describeError(err: unknown): string {
  if (err instanceof ApiException) return err.apiError.message;
  if (err instanceof Error) return err.message;
  return "Something went wrong";
}

export function WebhookSubscribeForm(): JSX.Element {
  const [url, setUrl] = useState("");
  const [email, setEmail] = useState("");
  const [token, setToken] = useState("");
  const [phase, setPhase] = useState<Phase>({ kind: "idle" });
  const [submitting, setSubmitting] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit() {
    setError(null);
    setSubmitting(true);
    try {
      const out = await api.subscribeWebhook(url.trim(), email.trim() || undefined);
      setPhase({ kind: "verify_pending", result: out });
      // Pre-fill the verification field with the token from the
      // response. In production the subscriber receives this token at
      // their endpoint via the verification ping and echoes it back
      // through their own UI; we expose it here so operators can
      // complete the loop end-to-end without leaving the page.
      setToken(out.verificationToken);
    } catch (err) {
      setError(describeError(err));
    } finally {
      setSubmitting(false);
    }
  }

  async function verify() {
    setError(null);
    setVerifying(true);
    try {
      await api.verifyWebhook(token.trim());
      setPhase({ kind: "verified" });
    } catch (err) {
      setError(describeError(err));
    } finally {
      setVerifying(false);
    }
  }

  if (phase.kind === "verified") {
    return (
      <div className="rounded-md border border-status-ok/40 bg-status-ok/10 p-4 text-sm text-status-ok">
        Webhook verified — incident events will now be delivered to your endpoint.
      </div>
    );
  }

  if (phase.kind === "verify_pending") {
    return (
      <div className="space-y-3">
        <div className="rounded-md border border-status-degraded/40 bg-status-degraded/10 p-3 text-xs text-status-degraded">
          We sent a signed verification ping to your URL. Echo the token
          back to confirm you control the endpoint.
        </div>
        <div>
          <label className="block text-xs uppercase tracking-wide text-text-muted">
            Verification token
          </label>
          <input
            value={token}
            onChange={(e) => setToken(e.target.value)}
            className="mt-1 w-full rounded-md border border-border bg-bg px-3 py-2 font-mono text-sm"
          />
        </div>
        <div className="grid gap-2 sm:grid-cols-2 sm:text-xs">
          <div className="rounded-md border border-border bg-bg-elevated p-3">
            <p className="text-text-muted">Subscription id</p>
            <p className="font-mono">{phase.result.id}</p>
          </div>
          <div className="rounded-md border border-border bg-bg-elevated p-3">
            <p className="text-text-muted">Secret (store it now)</p>
            <p className="break-all font-mono">{phase.result.secret}</p>
          </div>
        </div>
        {error && <p className="text-sm text-status-major">{error}</p>}
        <div className="flex gap-2">
          <button
            onClick={() => void verify()}
            disabled={verifying || token.trim().length === 0}
            className="rounded-md bg-accent px-4 py-2 text-sm font-medium text-bg hover:bg-accent-hover disabled:opacity-50"
          >
            {verifying ? "Verifying…" : "Verify"}
          </button>
          <button
            onClick={() => {
              setPhase({ kind: "idle" });
              setToken("");
              setError(null);
            }}
            className="rounded-md border border-border px-4 py-2 text-sm text-text-secondary"
          >
            Start over
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div>
        <label className="block text-xs uppercase tracking-wide text-text-muted">
          Webhook URL
        </label>
        <input
          type="url"
          required
          value={url}
          onChange={(e) => setUrl(e.target.value)}
          placeholder="https://example.com/hooks/dr-status"
          className="mt-1 w-full rounded-md border border-border bg-bg px-3 py-2 text-sm"
        />
      </div>
      <div>
        <label className="block text-xs uppercase tracking-wide text-text-muted">
          Email (optional)
        </label>
        <input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="ops@example.com"
          className="mt-1 w-full rounded-md border border-border bg-bg px-3 py-2 text-sm"
        />
      </div>
      {error && <p className="text-sm text-status-major">{error}</p>}
      <button
        onClick={() => void submit()}
        disabled={submitting || url.trim().length === 0}
        className="rounded-md bg-accent px-4 py-2 text-sm font-medium text-bg hover:bg-accent-hover disabled:opacity-50"
      >
        {submitting ? "Submitting…" : "Subscribe"}
      </button>
    </div>
  );
}
